import { createContext } from 'react';
import { decorate, observable, action, computed } from 'mobx';
import moment from 'moment';

import api from '../api';
import * as wh from '../utilities/workOrderHelper';
import * as fn from '../utilities/_functions';

export class PurchaseView {
    id = null;
    data = null;
    taxRates = [];
    isSaving = false;
    isLoading = false;
    startWorkOrder = false;

    cancelWorkOrderSearch = null;
    cancelPurchaseOrder = null;
    cancelPurchaseStart = null;
    cancelPurchaseSave = null;

    start = (customer) => {
        const that = this;

        this.clear();
        this.isLoading = true;

        return new Promise((resolve, reject) => {
            Promise.all([
                api.Purchases.start(customer.id, (c) => { that.cancelPurchaseStart = c }),
                that.loadTaxRates(),
            ])
                .then((response) => {
                    if (response && response.length > 0) {
                        that.data = response[0].data;
                        that.data.dueDate = moment().startOf('day');
                    }
                    resolve();
                })
                .catch(() => {
                    reject();
                })
                .finally(() => {
                    that.isLoading = false;
                    that.isReady = true;
                })
        })
    }

    load = (id, clear = true) => {
        const that = this;

        if (clear) {
            this.clear();
        }

        this.id = id;
        this.isLoading = true;

        return new Promise((resolve, reject) => {
            Promise.all([
                api.Purchases.get(id, (c) => { that.cancelPurchaseOrder = c }),
                that.loadTaxRates()
            ])
                .then((response) => {
                    if (response && response.length > 0) {
                        that.data = response[0].data;
                    }
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isLoading = false;
                    that.isReady = true;
                })
        })
    }

    reload = () => {
        if (!this.id) return Promise.reject();

        const id = this.id;
        this.id = null;

        return this.load(id, false);
    }

    loadTaxRates = () => {
        const that = this;
        this.isLoading = true;

        return new Promise((resolve, reject) => {
            api.TaxRates.get(moment().format('YYYY-MM-DD'), (c) => { that.cancelTaxRates = c })
                .then(({ data: taxRateData }) => {
                    that.taxRates = taxRateData;
                    resolve();
                })
                .catch(() => {
                    reject();
                })
                .finally(() => {
                    that.isLoading = false;
                })
        })
    }

    convertStagedWorkOrders = () => {
        const that = this;
        this.isSaving = true;

        return new Promise((resolve, reject) => {
            const statusRequests = that.stagedWorkOrders.map(wo => { return api.WorkOrders.status(wo.id, { status: 'Incomplete' }) });

            Promise.all(statusRequests)
                .then(() => {
                    resolve();
                })
                .catch(() => {
                    reject();
                })
                .finally(() => {
                    that.isSaving = false;
                })
        })
    }

    voidAdjustment = () => {
        const that = this;

        return new Promise((resolve, reject) => {
            if (that.data.status === 'Voided') {
                this.isSaving = true;

                api.Purchases.adjustment(that.id, {}, (c) => { that.cancelPurchaseSave = c })
                    .then(() => {
                        that.reload();
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    })
                    .finally(() => {
                        that.isSaving = false;
                    })
            }
            else {
                resolve();
            }
        })
    }

    clear = () => {
        this.id = null;
        this.data = null;
        this.startWorkOrder = false;
        this.isSaving = false;
        this.isLoading = false;
        this.isReady = false;

        if (fn.isFunction(this.cancelWorkOrderSearch)) {
            this.cancelWorkOrderSearch();
            this.cancelWorkOrderSearch = null;
        }

        if (fn.isFunction(this.cancelPurchaseOrder)) {
            this.cancelPurchaseOrder();
            this.cancelPurchaseOrder = null;
        }

        if (fn.isFunction(this.cancelPurchaseStart)) {
            this.cancelPurchaseStart();
            this.cancelPurchaseStart = null;
        }

        if (fn.isFunction(this.cancelPurchaseSave)) {
            this.cancelPurchaseSave();
            this.cancelPurchaseSave = null;
        }
    }

    get isNew() {
        return this.id === null;
    }

    get taxRate1() {
        const item = this.data && this.data.items && this.data.items.filter(i => i.isTaxable && i.taxRate1)[0];
        return (item ? item.taxRate1 : 0);
    }

    get taxRate2() {
        const item = this.data && this.data.items && this.data.items.filter(i => i.isTaxable && i.taxRate2)[0];
        return (item ? item.taxRate2 : 0);
    }

    get taxRate3() {
        const item = this.data && this.data.items && this.data.items.filter(i => i.isTaxable && i.taxRate3)[0];
        return (item ? item.taxRate3 : 0);
    }

    get totalTaxRate() {
        return (this.taxRate1 + this.taxRate2 + this.taxRate3);
    }

    get stagedWorkOrders() {
        return this.data && this.data.workOrders && this.data.workOrders.some(wo => wh.isStaged(wo)) ? this.data.workOrders.filter(wo => wh.isStaged(wo)) : []
    }
}

decorate(PurchaseView, {
    id: observable,
    data: observable,
    isNew: computed,
    isSaving: observable,
    isLoading: observable,
    isReady: observable,
    startWorkOrder: observable,
    load: action,
    reload: action,
    loadTaxRates: action,
    convertStagedWorkOrders: action,
    voidAdjustment: action,
    clear: action,
    taxRate1: computed,
    taxRate2: computed,
    taxRate3: computed,
    totalTaxRate: computed,
    stagedWorkOrders: computed,
})

export default createContext(new PurchaseView());